// ═══════════════════════════════════════════════════════════════════════════════
// Main app top bar — brand + notification (no language / avatar per UI spec)
// ═══════════════════════════════════════════════════════════════════════════════

import { APP_NAME, USER_ROLE } from '@fdh/shared';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { APP_ICONS } from '../../lib/iconAssets';
import { MainMenuButton } from '../MainMenuButton';
import { NotificationBell } from '../NotificationBell';

type AppTopBarProps = {
  isHidden?: boolean;
};

export const AppTopBar = ({ isHidden = false }: AppTopBarProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleGoHome = () => {
    const homePath = user?.role === USER_ROLE.EMPLOYER ? '/dashboard' : '/home';
    navigate(homePath);
  };

  const handleBrandKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleGoHome();
    }
  };

  return (
    <header
      className={[
        'fixed top-[var(--fdh-play-banner-h,0px)] left-0 right-0 z-20 border-b border-gray-200/80 bg-white pt-[env(safe-area-inset-top,0px)] transition-transform duration-200 ease-out dark:border-gray-700 dark:bg-gray-900',
        isHidden ? '-translate-y-full pointer-events-none' : 'translate-y-0',
      ].join(' ')}
      aria-hidden={isHidden}
    >
      <div className="relative mx-auto flex h-[56px] max-w-lg items-center justify-between px-4">
        <div className="flex min-w-0 items-center">
          <MainMenuButton panelSide="left" triggerSize="header" />
        </div>
        <button
          type="button"
          onClick={handleGoHome}
          onKeyDown={handleBrandKeyDown}
          tabIndex={0}
          aria-label={APP_NAME}
          className="absolute left-1/2 -translate-x-1/2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          <img
            src={APP_ICONS.menu}
            alt={APP_NAME}
            className="h-[48.4px] w-[48.4px] shrink-0 rounded-lg object-contain dark:[filter:drop-shadow(0_0_15px_rgba(107,114,128,0.5))_drop-shadow(0_0_5px_rgba(107,114,128,0.4))]"
          />
        </button>
        <div className="flex shrink-0 items-center gap-2">
          <NotificationBell size="header" />
        </div>
      </div>
    </header>
  );
};
